import {
  ArchiveBoxIcon,
  CheckCircleIcon,
  CheckmarkIcon,
  ClockFilledIcon,
  PackageIcon,
  PackageOutlineIcon,
  PauseCircleIcon,
  RefundSmallIcon,
  TruckIcon,
  WarningIcon,
} from '@c/icons';
import Chip from '@ui/Chip';

export type StatusType =
  | 'completed'
  | 'delivered'
  | 'in-transit'
  | 'awaiting-shipment'
  | 'awaiting-dropoff'
  | 'order-placed'
  | 'cancelled'
  | 'refunded'
  | 'delivery-error'
  | 'on-hold'
  | 'dropped-off';

export const orderStateNumberToStateType = (
  state: number,
  rate_id?: string,
  dropped_off?: boolean
) => {
  switch (state) {
    case 0:
      return dropped_off
        ? 'dropped-off'
        : rate_id
        ? 'awaiting-dropoff'
        : 'awaiting-shipment';
    case 1:
      return 'in-transit';
    case 2:
      return 'delivered';
    case 3:
      return 'completed';
    case 4:
      return 'cancelled';
    case 5:
      return 'refunded';
    case 6:
      return 'delivery-error';
    case 7:
      return 'on-hold';
    default:
      return 'awaiting-shipment';
  }
};

export interface StatusBadgeProps {
  status: StatusType;
  rate_id?: string;
  is_partial?: boolean;
}
export const StatusBadge = ({ status, is_partial }: StatusBadgeProps) => {
  if (status === 'completed') {
    return (
      <div className="flex flex-col items-start gap-[1.6rem]">
        <div className="flex gap-[0.4rem]">
          <CheckCircleIcon />
          <span>Completed</span>
        </div>
      </div>
    );
  } else if (status === 'in-transit') {
    return (
      <div className="flex flex-col items-start gap-[1.6rem]">
        <div className="flex gap-[0.4rem]">
          <TruckIcon />
          <span>In Transit</span>
        </div>
      </div>
    );
  } else if (status === 'dropped-off') {
    return (
      <div className="flex flex-col items-start gap-[1.6rem]">
        <div className="flex gap-[0.4rem]">
          <CheckmarkIcon />
          <span>Dropped Off</span>
        </div>
      </div>
    );
  } else if (status === 'awaiting-dropoff' || status === 'awaiting-shipment') {
    return (
      <div className="flex flex-col items-start gap-[1.6rem]">
        <div className="flex items-center gap-[0.4rem] ">
          <ClockFilledIcon />
          <span>Awaiting Shipment</span>
        </div>
      </div>
    );
  } else if (status === 'order-placed') {
    return (
      <div className="flex flex-col items-start gap-[1.6rem]">
        <div className="flex gap-[0.4rem]">
          <PackageOutlineIcon />
          <span>Order Placed</span>
        </div>
      </div>
    );
  } else if (status === 'delivered') {
    // delivered
    return (
      <div className="flex flex-col items-start gap-[1.6rem]">
        <div className="flex gap-[0.4rem]">
          <ArchiveBoxIcon />
          <span>Package Delivered</span>
        </div>
      </div>
    );
  } else if (status === 'on-hold') {
    return (
      <div className="flex items-center gap-[0.4rem]">
        <PauseCircleIcon />
        <span className="text-[1.3rem]">On Hold</span>
      </div>
    );
  } else if (status === 'delivery-error') {
    return (
      <div className="flex items-center gap-[0.4rem]">
        <WarningIcon />
        <span className="text-[1.3rem]">Delivery Error</span>
      </div>
    );
  } else if (status === 'cancelled') {
    return (
      <div className="flex items-center gap-[0.4rem]">
        <WarningIcon />
        <span className="text-[1.3rem]">Cancelled</span>
      </div>
    );
  } else if (status === 'refunded') {
    return (
      <div className="flex items-center gap-[0.4rem] text-blue-500">
        <RefundSmallIcon />
        <span className="text-[1.3rem] text-black">
          {is_partial ? 'Partially Refunded' : 'Refunded'}
        </span>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-start gap-[1.6rem]">
      <div className="flex gap-[0.4rem]">
        <span>{status}</span>
      </div>
    </div>
  );
};

export interface PaymentStatusBadgeProps {
  status: 'paid' | 'unpaid' | 'declined' | 'cancelled' | 'refunded';
  showCustomer?: boolean;
}

export const PaymentStatusBadge = ({
  status,
  showCustomer,
}: PaymentStatusBadgeProps) => {
  switch (status) {
    case 'paid':
      return (
        <Chip text={showCustomer ? 'Customer Paid' : 'Paid'} color="success" />
      );
    case 'unpaid':
      return <Chip text="Unpaid" color="error" />;
    case 'declined':
      return <Chip text="Declined" color="gray" />;
    case 'cancelled':
      return <Chip text="Cancelled" color="error" />;
    case 'refunded':
      return <Chip text="Refunded" color="blue" />;
  }
};
